import m from "mithril";
import { getMithril, exportToXLSX, toExcelNumber, toExcelInt } from "../utils.js";
import toast from "../js/toaster.js";
import DaftarPinjamanList from "./components/DaftarPinjamanList.js";
import RekapAngsuran from "./components/RekapAngsuran.js";

const makeState = () => ({
    filters: {},
    data: [],
    loading: true,
    expandedRowId: null,
    details: {}, // { rowId: rows }
    loadingDetails: {}, // { rowId: boolean }
    extra: null, // e.g. recap grand totals
    page: 1,
    limit: 10,
    totalPages: 1,
    totalRecords: 0
});

const DaftarPinjaman = {
    oninit: function () {
        this.activeTab = "daftar"; // "daftar" or "rekap"
        this.rekapLoaded = false;

        this.daftar = makeState();
        this.daftar.toggle = (id) => this.toggle("daftar", id);
        this.daftar.goToPage = (page) => this.goToPage("daftar", page);
        this.daftar.refresh = () => this.refresh("daftar");

        this.rekap = makeState();
        this.rekap.exporting = false;
        this.rekap.toggle = (id) => this.toggle("rekap", id);
        this.rekap.goToPage = (page) => this.goToPage("rekap", page);
        this.rekap.refresh = () => this.refresh("rekap");
        this.rekap.exportExcel = () => this.exportRekap();

        this.loadSummary("daftar");
    },

    rekapParams: function (extra = {}) {
        const params = new URLSearchParams(extra);
        if (this.rekap.filters.startDate) params.set("startDate", this.rekap.filters.startDate);
        if (this.rekap.filters.endDate) params.set("endDate", this.rekap.filters.endDate);
        if (this.rekap.filters.search) params.set("search", this.rekap.filters.search);
        return params;
    },

    summaryUrl: function (tab) {
        if (tab === "daftar") {
            const search = this.daftar.filters.search ? `&search=${encodeURIComponent(this.daftar.filters.search)}` : "";
            return `/api/loan-list?mode=summary&page=${this.daftar.page}&limit=${this.daftar.limit}${search}`;
        }
        const params = this.rekapParams({ page: this.rekap.page, limit: this.rekap.limit });
        return `/api/loan-recap?${params.toString()}`;
    },

    detailUrl: function (tab, rowId) {
        if (tab === "daftar") return `/api/loan-list?customerId=${rowId}`;
        const params = this.rekapParams({ customerId: rowId });
        return `/api/loan-recap?${params.toString()}`;
    },

    loadSummary: async function (tab) {
        const state = this[tab];
        state.loading = true;
        try {
            const response = await getMithril(this.summaryUrl(tab));
            if (response.success) {
                state.data = response.data || [];
                if (response.pagination) {
                    state.page = response.pagination.currentPage;
                    state.totalPages = response.pagination.totalPages;
                    state.totalRecords = response.pagination.totalRecords;
                }
                if (response.totals) state.extra = response.totals;
            } else {
                toast.show(response.error || "Gagal memuat data", "error");
            }
        } catch (error) {
            console.error(error);
            toast.show("Terjadi kesalahan sistem", "error");
        } finally {
            state.loading = false;
            m.redraw();
        }
    },

    loadDetail: async function (tab, rowId) {
        const state = this[tab];
        if (state.details[rowId]) return; // Already loaded

        state.loadingDetails[rowId] = true;
        try {
            const response = await getMithril(this.detailUrl(tab, rowId));
            if (tab === "rekap") {
                state.details[rowId] = {
                    payments: (response && response.success && response.data) || [],
                    settlements: (response && response.success && response.settlements) || []
                };
            } else {
                state.details[rowId] = (response && response.success && response.data) || [];
            }
        } catch (error) {
            console.error(error);
            toast.show("Terjadi kesalahan saat memuat rincian", "error");
        } finally {
            state.loadingDetails[rowId] = false;
            m.redraw();
        }
    },

    toggle: function (tab, rowId) {
        const state = this[tab];
        if (state.expandedRowId === rowId) {
            state.expandedRowId = null;
        } else {
            state.expandedRowId = rowId;
            this.loadDetail(tab, rowId);
        }
    },

    refresh: function (tab) {
        const state = this[tab];
        state.page = 1;
        state.expandedRowId = null;
        state.details = {};
        this.loadSummary(tab);
    },

    goToPage: function (tab, page) {
        const state = this[tab];
        state.page = page;
        state.expandedRowId = null;
        this.loadSummary(tab);
    },

    exportRekap: async function () {
        const f = this.rekap.filters;
        const params = this.rekapParams({ export: "1" });

        this.rekap.exporting = true;
        m.redraw();
        try {
            const response = await getMithril(`/api/loan-recap?${params.toString()}`);
            if (!response.success) {
                toast.show(response.error || "Gagal mengekspor data", "error");
                return;
            }
            const rows = (response.data || []).map(r => [
                r.no_reg || "-",
                r.nama_nasabah || "-",
                toExcelInt(r.loan_count),
                toExcelInt(r.payment_count),
                toExcelNumber(r.total_principal),
                toExcelNumber(r.total_interest),
                toExcelNumber(r.settlement_interest),
                toExcelNumber(r.total_interest_incl_settlement ?? ((Number(r.total_interest) || 0) + (Number(r.settlement_interest) || 0))),
                toExcelNumber(r.total_paid)
            ]);
            const totals = response.totals || {};
            if (rows.length > 0) {
                rows.push(["", "TOTAL", "", toExcelInt(totals.total_payments), toExcelNumber(totals.total_principal), toExcelNumber(totals.total_interest), toExcelNumber(totals.total_settlement_interest), toExcelNumber(totals.total_interest_incl_settlement ?? ((Number(totals.total_interest) || 0) + (Number(totals.total_settlement_interest) || 0))), toExcelNumber(totals.total_paid)]);
            }

            const period = [f.startDate, f.endDate].filter(Boolean).join("_sd_");
            exportToXLSX(`Rekap_Angsuran${period ? "_" + period : ""}`, ["No. Reg", "Nama Nasabah", "Jml Pinjaman", "Jml Bayar", "Pokok", "Bunga Angsuran", "Bunga Pelunasan", "Total Jasa", "Total Angsuran"], rows);
            toast.show("Export berhasil", "success");
        } catch (error) {
            console.error(error);
            toast.show("Terjadi kesalahan saat mengekspor", "error");
        } finally {
            this.rekap.exporting = false;
            m.redraw();
        }
    },

    switchTab: function (tab) {
        this.activeTab = tab;
        if (tab === "rekap" && !this.rekapLoaded) {
            this.rekapLoaded = true;
            this.loadSummary("rekap");
        }
    },

    view: function () {
        const tabClass = (active) =>
            "whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm " +
            (active
                ? "border-orange-500 text-orange-600 dark:text-orange-400"
                : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300");

        return m("div", { class: "p-6" }, [
            m("h1", { class: "text-2xl font-bold text-gray-800 dark:text-white mb-6" }, "Daftar Pinjaman"),

            m("div", { class: "mb-6 border-b border-gray-200 dark:border-gray-700" }, [
                m("nav", { class: "-mb-px flex space-x-8" }, [
                    m("button", {
                        class: tabClass(this.activeTab === "daftar"),
                        onclick: () => this.switchTab("daftar")
                    }, "Daftar Pinjaman"),
                    m("button", {
                        class: tabClass(this.activeTab === "rekap"),
                        onclick: () => this.switchTab("rekap")
                    }, "Rekap Angsuran")
                ])
            ]),

            this.activeTab === "daftar" && m(DaftarPinjamanList, { state: this.daftar }),
            this.activeTab === "rekap" && m(RekapAngsuran, { state: this.rekap })
        ]);
    }
};

export default DaftarPinjaman;
